
import { defineComponent, ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import StarXpGate from '@/components/star/StarXpGate.vue'
import { isXpLowVersion } from '@/utils/xpEnv'
import StarTabBar from '@/components/star/StarTabBar.vue'
import QuantSelect from '@/components/quantSelect.vue'
import { useStarTheme, useStarRouter } from '@/utils/useStarTheme'
import quantApi from '@/api/quantApi'
import { useStarTip } from '@/utils/useStarTip'
import ws from '@/websocket/index'

const __default__ = defineComponent({
  name: 'StarStock',
  components: { StarXpGate, StarTabBar, QuantSelect },
  setup() {
    const route = useRoute()
    const { colors, isDark, isLight } = useStarTheme()
    const { starPush } = useStarRouter()
    const { handleOneClickBacktestStatus } = useStarTip()

    const selectedStock = ref<any>({})
    const activeTopTab = ref('home')

    // 接口数据
    const stockOneOptions = ref<any>(null)  // quantApi.getStockOneOptions

    // 自选股列表（从接口获取）
    const stockList = ref<any[]>([])

    // 买入/卖出/回测周期列表
    const buyList = computed(() => stockOneOptions.value?.buyList || [])
    console.log('buyList',buyList)
    const factorList = computed(() => stockOneOptions.value?.sellList || [])
    const periodList = computed(() => stockOneOptions.value?.periodList || [])

    // 选中状态：{ factorCode, factorName, optionCode, optionName }
    const selectedBuy = ref<any>(null)
    const selectedSell = ref<any>(null)
    const selectedPeriod = ref<string>('')
    const customDateRange = ref<any>(null)

    // 浮层状�?
    const buyPopover = ref<string>('')   // 当前展开 options �?factorCode（买入）
    const sellPopover = ref<string>('')  // 当前展开 options �?factorCode（卖出）

    const openBuyPopover = (factor: any) => {
      sellPopover.value = ''
      if (!factor.options?.length) {
        // 无子选项直接切换选中
        if (selectedBuy.value?.factorCode === factor.factorCode) {
          selectedBuy.value = null
        } else {
          selectedBuy.value = { factorCode: factor.factorCode, factorName: factor.factorName, optionCode: '', optionName: '' }
        }
        buyPopover.value = ''
        return
      }
      buyPopover.value = buyPopover.value === factor.factorCode ? '' : factor.factorCode
      // 预填已选值
      pendingBuyOption.value = selectedBuy.value?.factorCode === factor.factorCode
        ? { factor, opt: { optionCode: selectedBuy.value.optionCode, optionName: selectedBuy.value.optionName } }
        : null
    }
    const openSellPopover = (factor: any) => {
      buyPopover.value = ''
      if (!factor.options?.length) {
        // 无子选项直接切换选中
        if (selectedSell.value?.factorCode === factor.factorCode) {
          selectedSell.value = null
        } else {
          selectedSell.value = { factorCode: factor.factorCode, factorName: factor.factorName, optionCode: '', optionName: '' }
        }
        sellPopover.value = ''
        return
      }
      sellPopover.value = sellPopover.value === factor.factorCode ? '' : factor.factorCode
      pendingSellOption.value = selectedSell.value?.factorCode === factor.factorCode
        ? { factor, opt: { optionCode: selectedSell.value.optionCode, optionName: selectedSell.value.optionName } }
        : null
    }

    const pendingBuyOption = ref<any>(null)
    const pendingSellOption = ref<any>(null)

    const selectBuyOption = (factor: any, option: any) => {
      if (selectedSell.value?.factorCode === factor.factorCode && selectedSell.value?.optionCode === option.optionCode) {
        showToastMsg('买卖规则不可相同')
        return
      }
      selectedBuy.value = { factorCode: factor.factorCode, factorName: factor.factorName, optionCode: option.optionCode, optionName: option.optionName }
      buyPopover.value = ''
    }
    const selectSellOption = (factor: any, option: any) => {
      if (selectedBuy.value?.factorCode === factor.factorCode && selectedBuy.value?.optionCode === option.optionCode) {
        showToastMsg('买卖规则不可相同')
        return
      }
      selectedSell.value = { factorCode: factor.factorCode, factorName: factor.factorName, optionCode: option.optionCode, optionName: option.optionName }
      sellPopover.value = ''
    }

    const confirmBuyOption = () => {
      if (!pendingBuyOption.value) { buyPopover.value = ''; return }
      selectBuyOption(pendingBuyOption.value.factor, pendingBuyOption.value.opt)
      pendingBuyOption.value = null
    }
    const confirmSellOption = () => {
      if (!pendingSellOption.value) { sellPopover.value = ''; return }
      selectSellOption(pendingSellOption.value.factor, pendingSellOption.value.opt)
      pendingSellOption.value = null
    }

    const clearBuy = () => { selectedBuy.value = null }
    const clearSell = () => { selectedSell.value = null }

    const selectPeriod = (code: string) => {
      selectedPeriod.value = selectedPeriod.value === code ? '' : code
      if (selectedPeriod.value !== 'custom') customDateRange.value = null
    }

    const onCustomDateChange = (dates: any) => {
      customDateRange.value = dates
      if (dates && dates.length === 2 && dates[0] && dates[1]) {
        selectedPeriod.value = 'custom'
      }
    }

    // 策略摘要
    const summaryStock = computed(() => selectedStock.value?.securityNo ? `${selectedStock.value.securityNo} ${selectedStock.value.securityName}` : '—')
    const summaryBuy = computed(() => selectedBuy.value ? (selectedBuy.value.optionName ? `${selectedBuy.value.factorName}:${selectedBuy.value.optionName}` : selectedBuy.value.factorName) : '—')
    const summarySell = computed(() => selectedSell.value ? (selectedSell.value.optionName ? `${selectedSell.value.factorName}:${selectedSell.value.optionName}` : selectedSell.value.factorName) : '—')
    const summaryPeriod = computed(() => {
      if (selectedPeriod.value === 'custom' && customDateRange.value?.length === 2) {
        const fmt = (d: any) => d?.format ? d.format('YYYY/MM/DD') : String(d).slice(0, 10)
        return `${fmt(customDateRange.value[0])}-${fmt(customDateRange.value[1])}`
      }
      return periodList.value.find((p: any) => p.code === selectedPeriod.value)?.name || '—'
    })

    const canBacktest = computed(() =>
      !!selectedStock.value?.securityNo &&
      !!selectedBuy.value &&
      !!selectedSell.value &&
      !!selectedPeriod.value
    )

    const goHome = () => starPush('/star')
    const goResult = async () => {
      if (!selectedStock.value?.securityNo) { showToastMsg('第一步：请先设定股票'); return }
      if (!selectedBuy.value) { showToastMsg('第二步：请选择买入规则'); return }
      if (!selectedSell.value) { showToastMsg('第三步：请选择卖出规则'); return }
      if (!selectedPeriod.value) { showToastMsg('第四步：请选择回测周期'); return }
      globalLoading.value = true
      try {
        const token = route.query.token as string
        const fmt = (d: any) => d?.valueOf ? d.valueOf() : 0
        const res = await quantApi.doTestCustom({
          data: {
            market: (selectedStock.value.securityMarket || '').toLowerCase(),
            symbol: selectedStock.value.securityNo,
            factorBuy: selectedBuy.value ? [{ factorCode: selectedBuy.value.factorCode, options: selectedBuy.value.optionCode ? [{ optionCode: selectedBuy.value.optionCode }] : [] }] : [],
            factorSell: selectedSell.value ? [{ factorCode: selectedSell.value.factorCode, options: selectedSell.value.optionCode ? [{ optionCode: selectedSell.value.optionCode }] : [] }] : [],
            period: selectedPeriod.value || 'month3',
            periodStart: selectedPeriod.value === 'custom' && customDateRange.value ? fmt(customDateRange.value[0]) : 0,
            periodEnd: selectedPeriod.value === 'custom' && customDateRange.value ? fmt(customDateRange.value[1]) : 0,
          },
          headers: { token }
        })
        const { status, toast, taskID: resTaskID, articleID } = res?.data || {}
        handleOneClickBacktestStatus(status, toast, () => {
          starPush('/star/stock/result', { taskID: resTaskID, quantType: '1', isfrom: '1',isocbt: '1' })
        }, { articleID })
      } catch (e) { /* 静默失败 */ } finally { globalLoading.value = false }
    }

    const showOptional = ref(false)
    const showToast = ref(false)
    const toastMsg = ref('')
    const globalLoading = ref(false)

    const handleStockSelect = (stock: any) => {
      console.log('handleStockSelect',stock)
      if (!stock || (!stock.Code && !stock.securityNo)) {
        selectedStock.value = {}
        return
      }
      selectedStock.value = {
        securityNo: stock.Code || stock.securityNo || '',
        securityName: stock.Name || stock.securityName || '',
        securityMarket: (stock.MarketCode || stock.securityMarket || '').toLowerCase(),
      }
    }

    const showToastMsg = (msg: string) => {
      toastMsg.value = msg
      showToast.value = true
      setTimeout(() => { showToast.value = false }, 2000)
    }

    const handleAiQuant = async () => {
      if (!selectedStock.value?.securityNo) {
        showToastMsg('请先设定股票后进行智能量化')
        return
      }
      globalLoading.value = true
      try {
        const token = route.query.token as string
        const res = await quantApi.doTestAi({
          data: {
            market: (selectedStock.value.securityMarket || '').toLowerCase(),
            symbol: selectedStock.value.securityNo
          },
          headers: { token }
        })
        const { status, toast, taskID: resTaskID, articleID } = res?.data || {}
        handleOneClickBacktestStatus(status, toast, () => {
          starPush('/star/stock/result', { taskID: resTaskID, quantType: '2', isfrom: '1',isocbt: '1' })
        }, { articleID })
      } catch (e) { /* 静默失败 */ } finally { globalLoading.value = false }
    }

    const fetchData = async () => {
      try {
        const token = route.query.token as string
        const [optionsRes, stocksRes] = await Promise.all([
          quantApi.getStockOneOptions({ headers: { token } }),
          quantApi.getMyOptionalStocks({ headers: { token } })
        ])
        stockOneOptions.value = optionsRes?.data || null
        const list: any[] = stocksRes?.data?.List || []
        stockList.value = list.map((s: any) => ({
          securityNo: s.symbol || '',
          securityName: s.name || '',
          optionalPrice: s.lastPx != null ? String(s.lastPx) : '',
          pxChangeRate: s.pxChangeRate != null ? s.pxChangeRate : null,
          securityMarket: (s.market || '').toLowerCase(),
        }))

        // 订阅自选股实时行情
        if (stockList.value.length) {
          const wsStockList = stockList.value.map(s => ({ symbol: s.securityNo, market: s.securityMarket }))
          ws.websocket.safeSubscribeList('star_stock_list', wsStockList)
        }
      } catch (e) { /* 静默失败 */ }
    }

    onMounted(() => {
      if (isXpLowVersion(route.query as Record<string, unknown>)) return
      fetchData()
      document.addEventListener('click', handleClickOutside)

      // 监听行情推送，更新最新价和涨跌幅
      ws.websocket.on('onHQLIST', (msg: any) => {
        if (!stockList.value.length) return
        msg?.QuoteData?.DynaData?.forEach((d: any) => {
          const stock = stockList.value.find(s => s.securityNo === d.InstrumentID)
          if (!stock) return
          if (d.LastPrice !== undefined) stock.optionalPrice = String(d.LastPrice)
          if (d.UpdownRate !== undefined) stock.pxChangeRate = d.UpdownRate
        })
      })
    })

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside)
      ws.websocket.safeUnsubscribeList('star_stock_list')
    })

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('.indicator-card-wrap')) {
        buyPopover.value = ''
        sellPopover.value = ''
      }
    }

    // 图片资源
    const breadcrumbArrow = ref(require('@/assets/asking/images/arrow_right.png'))
    const closeIcon = ref(require('@/assets/close.png'))
    const arrowDown = ref(require('@/assets/normal-down.png'))

    return {
      activeTopTab,
      colors, isDark, isLight,
      selectedStock, handleStockSelect,
      showOptional, showToast, toastMsg,
      globalLoading,
      handleAiQuant,
      stockList,
      buyList, factorList, periodList,
      selectedBuy, selectedSell, selectedPeriod,
      buyPopover, sellPopover,
      openBuyPopover, openSellPopover,
      selectBuyOption, selectSellOption,
      pendingBuyOption, pendingSellOption,
      confirmBuyOption, confirmSellOption,
      clearBuy, clearSell,
      customDateRange, onCustomDateChange,
      selectPeriod,
      summaryStock, summaryBuy, summarySell, summaryPeriod,
      canBacktest,
      goHome, goResult,
      breadcrumbArrow, closeIcon, arrowDown,
    }
  }
})  

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "13b36826": (_ctx.isLight ? "#f0f2f5" : _ctx.colors.bg),
  "681fa842": (_ctx.isLight ? "#f0f0f0" : "#0a0a0a"),
  "6db468cd": (_ctx.isLight ? "#e0e0e0" : _ctx.colors.divider),
  "74c76de8": (_ctx.isLight ? "rgb(153, 153, 153)" : "#fff"),
  "4dc76fe3": (_ctx.isDark ? "#c8c8c8" : "#4d4d4d"),
  "25837b5a": (_ctx.isDark ? "#ff5c5c" : "#cc2b1d"),
  "66f75cc6": (_ctx.isDark ? "#3ae265" : "#439731"),
  "81dac808": (_ctx.isLight ? "#f0f2f5" : "rgba(255, 255, 255, 0.07)"),
  "afe00cb8": (_ctx.isLight ? "#000" : "#fff"),
  "00eeb54b": (_ctx.isDark ? "#2f2e2e" : "#f5f5f5"),
  "6cebb928": (_ctx.isDark ? "#3d3d3d" : "#d9dbdd"),
  "6920b260": (_ctx.isDark ? "#555" : "#aaa"),
  "6b36910c": (_ctx.isDark ? "#fff" : "#666"),
  "e976c7f8": (_ctx.isDark ? "rgba(255,255,255,0.35)" : "#bbb"),
  "79b4606e": (_ctx.isDark ? "rgba(255,255,255,0.45)" : "#999")
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__